/**
 * /auth/register – Đăng ký tài khoản mới
 * Admin xét duyệt sau khi register (is_active = false mặc định)
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/core/database/supabase';
import { brandConfig } from '@/config/brand.config';

type Step = 'form' | 'success';

export default function RegisterPage() {
  const [step, setStep] = useState<Step>('form');
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm: '',
    job_title: '',
    phone: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPwd, setShowPwd] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
    if (error) setError(null);
  };

  const validate = (): string | null => {
    if (!form.full_name.trim()) return 'Vui lòng nhập họ và tên';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Email không hợp lệ';
    if (form.password.length < 8) return 'Mật khẩu phải ít nhất 8 ký tự';
    if (form.password !== form.confirm) return 'Mật khẩu xác nhận không khớp';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }

    setIsLoading(true); setError(null);
    try {
      // Bước 1: Tạo auth user
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        options: {
          data: {
            full_name: form.full_name.trim(),
            role_code: 'staff',
          },
          // Không auto-confirm, cần admin approve
        },
      });

      if (signUpError) {
        if (signUpError.message.includes('already registered')) {
          throw new Error('Email này đã được đăng ký. Vui lòng đăng nhập hoặc dùng email khác.');
        }
        throw signUpError;
      }

      if (!authData.user) throw new Error('Đăng ký thất bại, vui lòng thử lại.');

      // Bước 2: Upsert profile với is_active = false (chờ admin duyệt)
      await supabase.from('user_profiles').upsert({
        id: authData.user.id,
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        job_title: form.job_title.trim() || null,
        role_code: 'staff', // Default role
        is_active: false, // Chờ admin kích hoạt
        updated_at: new Date().toISOString(),
      }, { onConflict: 'id', ignoreDuplicates: false });

      // Đăng xuất ngay (tài khoản chưa active)
      await supabase.auth.signOut();

      setStep('success');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Đăng ký thất bại');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] flex-col relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=beautiful%20vietnamese%20landscape%20garden%20design%20with%20ornamental%20plants%20flower%20beds%20manicured%20hedges%20professional%20horticulture%20tropical%20garden%20architecture%20soft%20natural%20lighting%20elegant%20composition&width=520&height=900&seq=register-bg-001&orientation=portrait')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-green-900/70 via-green-800/60 to-green-900/80" />
        <div className="relative z-10 flex flex-col h-full p-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
              <i className="ri-leaf-line text-white text-xl" />
            </div>
            <div>
              <p className="text-white font-bold text-lg">{brandConfig.name}</p>
              <p className="text-green-200 text-xs">Quản lý cảnh quan</p>
            </div>
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <h1 className="text-white font-bold text-3xl xl:text-4xl leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Tham gia cùng<br />đội ngũ Landscape
            </h1>
            <p className="text-green-100 text-base leading-relaxed mb-8">
              Đăng ký tài khoản để bắt đầu quản lý dự án thi công và bảo dưỡng cảnh quan chuyên nghiệp.
            </p>
            <div className="space-y-3">
              {[
                { icon: 'ri-shield-check-line', text: 'Tài khoản được xét duyệt bởi admin' },
                { icon: 'ri-smartphone-line', text: 'Truy cập từ mọi thiết bị' },
                { icon: 'ri-team-line', text: 'Làm việc nhóm thời gian thực' },
                { icon: 'ri-lock-line', text: 'Bảo mật dữ liệu doanh nghiệp' },
              ].map(item => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center bg-white/15 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-green-200 text-sm`} />
                  </div>
                  <span className="text-green-100 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-green-300 text-xs">{brandConfig.copyright}</p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 bg-white overflow-y-auto">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <div className="w-9 h-9 flex items-center justify-center bg-green-600 rounded-xl">
              <i className="ri-leaf-line text-white text-lg" />
            </div>
            <div>
              <p className="font-bold text-slate-900">{brandConfig.name}</p>
              <p className="text-xs text-slate-500">Quản lý cảnh quan</p>
            </div>
          </div>

          {step === 'success' ? (
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-green-600 text-3xl" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Đăng ký thành công!
              </h2>
              <p className="text-sm text-slate-500 mb-2">
                Tài khoản của bạn đã được tạo và đang chờ admin xét duyệt.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Bạn sẽ nhận được email thông báo khi tài khoản được kích hoạt.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-start gap-2">
                  <i className="ri-time-line text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Đang chờ xét duyệt</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Quản trị viên sẽ kích hoạt tài khoản trong vòng 1–2 ngày làm việc.
                    </p>
                  </div>
                </div>
              </div>
              <Link to="/auth/login" className="block w-full h-11 flex items-center justify-center gap-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-login-box-line" />
                Quay lại đăng nhập
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-7">
                <h2 className="text-2xl font-bold text-slate-900 mb-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Tạo tài khoản mới
                </h2>
                <p className="text-sm text-slate-500">
                  Đăng ký để sử dụng hệ thống Landscape
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl mb-4">
                  <i className="ri-error-warning-line text-red-600 text-sm flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Full name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Họ và tên *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <i className="ri-user-line text-sm" />
                    </span>
                    <input name="full_name" type="text" value={form.full_name} onChange={handleChange}
                      placeholder="Nguyễn Văn A"
                      className="w-full h-11 pl-9 pr-4 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <i className="ri-mail-line text-sm" />
                    </span>
                    <input name="email" type="email" value={form.email} onChange={handleChange}
                      placeholder="ten@congty.vn"
                      className="w-full h-11 pl-9 pr-4 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                    />
                  </div>
                </div>

                {/* Phone + Job title side by side */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Điện thoại</label>
                    <input name="phone" type="tel" value={form.phone} onChange={handleChange}
                      placeholder="0912 345 678"
                      className="w-full h-11 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Chức danh</label>
                    <input name="job_title" type="text" value={form.job_title} onChange={handleChange}
                      placeholder="Nhân viên BĐ"
                      className="w-full h-11 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Mật khẩu *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <i className="ri-lock-line text-sm" />
                    </span>
                    <input name="password" type={showPwd ? 'text' : 'password'} value={form.password} onChange={handleChange}
                      placeholder="Ít nhất 8 ký tự"
                      className="w-full h-11 pl-9 pr-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                    />
                    <button type="button" onClick={() => setShowPwd(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer">
                      <i className={`${showPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} />
                    </button>
                  </div>
                </div>

                {/* Confirm */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Xác nhận mật khẩu *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <i className="ri-lock-2-line text-sm" />
                    </span>
                    <input name="confirm" type={showPwd ? 'text' : 'password'} value={form.confirm} onChange={handleChange}
                      placeholder="Nhập lại mật khẩu"
                      className="w-full h-11 pl-9 pr-4 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                    />
                  </div>
                </div>

                {/* Policy note */}
                <div className="text-xs text-slate-500 bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">
                  <i className="ri-information-line mr-1 text-slate-400" />
                  Tài khoản mới sẽ cần được admin xét duyệt trước khi có thể đăng nhập. Vai trò mặc định là <strong>Nhân viên bảo dưỡng</strong>.
                </div>

                <button type="submit" disabled={isLoading}
                  className="w-full h-11 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer">
                  {isLoading ? (
                    <><i className="ri-loader-4-line animate-spin" /> Đang tạo tài khoản...</>
                  ) : (
                    <><i className="ri-user-add-line" /> Đăng ký</>
                  )}
                </button>
              </form>

              <div className="mt-6 pt-5 border-t border-slate-100 text-center">
                <p className="text-sm text-slate-500">
                  Đã có tài khoản?{' '}
                  <Link to="/auth/login" className="text-green-600 font-medium hover:underline">
                    Đăng nhập
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
